import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';

const Members = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm.length >= 3) {
        performSearch();
      } else {
        setSearchResults([]);
        setError(null);
      }
    }, 300); // 300ms delay

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const performSearch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/search?q=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to search members');
      }
      
      const data = await response.json();
      setSearchResults(data.users || []);
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberClick = (memberId) => {
    navigate(`/members/${memberId}`);
  };

  const renderMemberCard = (member) => (
    <div 
      key={member.id} 
      className="member-card"
      onClick={() => handleMemberClick(member.id)}
    >
      <div className="member-avatar">
        {member.photoUrl ? (
          <img src={member.photoUrl} alt={member.name} />
        ) : (
          <div className="member-avatar-placeholder">
            {member.name?.charAt(0)?.toUpperCase()}
          </div>
        )}
      </div>
      <div className="member-info">
        <h3>{member.name}</h3>
      </div>
    </div>
  );

  return (
    <div className="container members-page">
      <div className="search-container">
        <div className="search-input-container">
          <input
            type="text"
            className="search-input"
            placeholder={t('members.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {loading && <div className="search-loading">Searching...</div>}
        </div>
        
        {searchTerm.length > 0 && searchTerm.length < 3 && (
          <div className="search-hint">
            {t('members.searchTooShort')}
          </div>
        )}
      </div>

      <div className="search-results">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {searchTerm.length >= 3 && searchResults.length === 0 && !loading && !error && (
          <div className="no-results">
            {t('members.noResults')}
          </div>
        )}
        
        {searchResults.length > 0 && (
          <div className="members-grid">
            {searchResults.map(renderMemberCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Members;
